/**
 * Dashboard-specific data fetching.
 * All numbers come from the server-aggregated summary (public.dashboard_summary)
 * served by the ops Worker at GET /api/ops/dashboard. The ISO week helpers below
 * are shared with the sidebar and the week-flow chart.
 */

// ── ISO week helpers ────────────────────────────────────────────────────────

/** Returns the ISO week number (1-53) for a given date. */
export function isoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/** Returns the ISO year (may differ from calendar year at year boundaries). */
export function isoWeekYear(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
}

/** The Monday of the ISO week that contains `date`. */
export function isoWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

// ── Server-aggregated dashboard summary ─────────────────────────────────────
// Scale-ready path: all math runs in Postgres (public.dashboard_summary) and
// is served via the ops Worker at GET /api/ops/dashboard. Numbers arrive as
// JSON numbers from jsonb; we coerce defensively with Number().

export type PortfolioFlag =
  | 'missing_price'
  | 'missing_points'
  | 'missing_fortnox'
  | 'unassigned_7d'
  | 'no_jobs_30d'
  | 'qc_failed_30d';

export interface WeekFlowRow {
  week_start: string;
  total_jobs: number;
  completed_jobs: number;
  total_points: number;
}

export interface DashboardKpis {
  pending_approvals: number;
  oldest_pending_days: number;
  completed_7d: number;
  week_points: number;
  invoiced_month: number;
  contract_total: number;
}

export interface PortfolioRow {
  id: string;
  name: string;
  price: number;
  jobs_30d: number;
  flags: PortfolioFlag[];
}

export interface DashboardPortfolio {
  total_count: number;
  total_contract: number;
  ok_count: number;
  top: PortfolioRow[];
  exceptions: PortfolioRow[];
}

export interface CrewLoadRow {
  user_id: string;
  name: string | null;
  jobs: number;
  points: number;
  kr_per_point: number | null;
}

export interface DashboardSignals {
  unassigned_7d: number;
  sites_missing_price: number;
  sites_missing_points: number;
  sites_missing_fortnox: number;
  sites_no_jobs_30d: number;
  qc_failed_30d: number;
  pending_approvals: number;
}

export interface TodayJobRow {
  id: string;
  site_id: string | null;
  site_name: string | null;
  cleaner_name: string | null;
  status: string;
}

export interface DashboardToday {
  total: number;
  jobs: TodayJobRow[];
}

export interface DashboardSummary {
  week_flow: WeekFlowRow[];
  kpis: DashboardKpis;
  portfolio: DashboardPortfolio;
  crew_load: CrewLoadRow[];
  signals: DashboardSignals;
  today: DashboardToday;
}

function isRec(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown): number {
  return Number(value) || 0;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function numOrNull(value: unknown): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const VALID_FLAGS: ReadonlySet<string> = new Set([
  'missing_price',
  'missing_points',
  'missing_fortnox',
  'unassigned_7d',
  'no_jobs_30d',
  'qc_failed_30d',
]);

function parseFlags(value: unknown): PortfolioFlag[] {
  if (!Array.isArray(value)) return [];
  return value.filter((f): f is PortfolioFlag => typeof f === 'string' && VALID_FLAGS.has(f));
}

function parsePortfolioRow(value: unknown): PortfolioRow {
  const r = isRec(value) ? value : {};
  return {
    id: str(r.id),
    name: str(r.name),
    price: num(r.price),
    jobs_30d: num(r.jobs_30d),
    flags: parseFlags(r.flags),
  };
}

function parseSummary(value: unknown): DashboardSummary {
  const root = isRec(value) ? value : {};

  const weekFlow = Array.isArray(root.week_flow) ? root.week_flow : [];
  const kpis = isRec(root.kpis) ? root.kpis : {};
  const portfolio = isRec(root.portfolio) ? root.portfolio : {};
  const crewLoad = Array.isArray(root.crew_load) ? root.crew_load : [];
  const signals = isRec(root.signals) ? root.signals : {};
  const today = isRec(root.today) ? root.today : {};

  const portfolioTop = Array.isArray(portfolio.top) ? portfolio.top : [];
  const portfolioExceptions = Array.isArray(portfolio.exceptions) ? portfolio.exceptions : [];
  const todayJobs = Array.isArray(today.jobs) ? today.jobs : [];

  return {
    week_flow: weekFlow.map((row) => {
      const r = isRec(row) ? row : {};
      return {
        week_start: str(r.week_start),
        total_jobs: num(r.total_jobs),
        completed_jobs: num(r.completed_jobs),
        total_points: num(r.total_points),
      };
    }),
    kpis: {
      pending_approvals: num(kpis.pending_approvals),
      // Absent on the pre-migration payload → num() coerces undefined to 0.
      oldest_pending_days: num(kpis.oldest_pending_days),
      completed_7d: num(kpis.completed_7d),
      week_points: num(kpis.week_points),
      invoiced_month: num(kpis.invoiced_month),
      contract_total: num(kpis.contract_total),
    },
    portfolio: {
      total_count: num(portfolio.total_count),
      total_contract: num(portfolio.total_contract),
      ok_count: num(portfolio.ok_count),
      top: portfolioTop.map(parsePortfolioRow),
      exceptions: portfolioExceptions.map(parsePortfolioRow),
    },
    crew_load: crewLoad.map((row) => {
      const r = isRec(row) ? row : {};
      return {
        user_id: str(r.user_id),
        name: strOrNull(r.name),
        jobs: num(r.jobs),
        points: num(r.points),
        kr_per_point: numOrNull(r.kr_per_point),
      };
    }),
    signals: {
      unassigned_7d: num(signals.unassigned_7d),
      sites_missing_price: num(signals.sites_missing_price),
      // Absent on the pre-migration payload → num() coerces undefined to 0.
      sites_missing_points: num(signals.sites_missing_points),
      sites_missing_fortnox: num(signals.sites_missing_fortnox),
      sites_no_jobs_30d: num(signals.sites_no_jobs_30d),
      qc_failed_30d: num(signals.qc_failed_30d),
      pending_approvals: num(signals.pending_approvals),
    },
    today: {
      total: num(today.total),
      jobs: todayJobs.map((row) => {
        const r = isRec(row) ? row : {};
        return {
          id: str(r.id),
          site_id: strOrNull(r.site_id),
          site_name: strOrNull(r.site_name),
          cleaner_name: strOrNull(r.cleaner_name),
          status: str(r.status),
        };
      }),
    },
  };
}

// ── Per-site avvikelser feed ────────────────────────────────────────────────
// GET /api/ops/avvikelser returns flags for EVERY active site (no top-12 /
// exceptions cap), so the BRF list view can badge each row. Zero-flag sites are
// present with an empty array; a site absent from the feed is "unknown".

export interface SiteAvvikelser {
  site_id: string;
  flags: PortfolioFlag[];
}

function parseSiteAvvikelser(value: unknown): SiteAvvikelser {
  const r = isRec(value) ? value : {};
  return {
    site_id: str(r.site_id),
    flags: parseFlags(r.flags),
  };
}

export async function fetchSiteAvvikelser(
  sessionToken: string | null,
): Promise<SiteAvvikelser[]> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (sessionToken) {
    headers.set('Authorization', `Bearer ${sessionToken}`);
  }

  const response = await fetch('/api/ops/avvikelser', { method: 'GET', headers });
  const rawText = await response.text();
  let parsed: unknown = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = rawText;
    }
  }

  if (!response.ok) {
    const message =
      isRec(parsed) && typeof parsed.error === 'string'
        ? parsed.error
        : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  const sites = isRec(parsed) && Array.isArray(parsed.sites) ? parsed.sites : [];
  return sites.map(parseSiteAvvikelser);
}

export async function fetchDashboardSummary(
  sessionToken: string | null,
): Promise<DashboardSummary> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (sessionToken) {
    headers.set('Authorization', `Bearer ${sessionToken}`);
  }

  const response = await fetch('/api/ops/dashboard', { method: 'GET', headers });
  const rawText = await response.text();
  let parsed: unknown = null;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = rawText;
    }
  }

  if (!response.ok) {
    const message =
      isRec(parsed) && typeof parsed.error === 'string'
        ? parsed.error
        : `${response.status} ${response.statusText}`;
    throw new Error(message);
  }

  const summary = isRec(parsed) ? parsed.summary : null;
  return parseSummary(summary);
}
